import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Modal from "../Modules/Modal";
import axiosInstance from "../../../axiosConfig";

function PlanItem({ plan, onEdit, onDelete }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md hover:border-blue-300 transition-all duration-200">
      {/* Card Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white">
            {plan.planName}
          </span>
          <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
            Seq: {plan.planSequence}
          </span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={() => onEdit(plan)}
            className="px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-md transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(plan.planId)}
            className="px-2.5 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-md transition-colors"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Features */}
      <div className="p-2">
        {plan.planFeatures && plan.planFeatures.length > 0 ? (
          <div className="grid grid-cols-2 gap-1">
            {plan.planFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-blue-50 border border-blue-100 rounded px-2 py-1"
              >
                <p
                  className="text-xs font-medium text-blue-800 truncate"
                  title={feature.featureName}
                >
                  {feature.featureName}
                </p>
                {feature.featureDescription && (
                  <p
                    className="text-xs text-blue-500 truncate"
                    title={feature.featureDescription}
                  >
                    {feature.featureDescription}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic px-1">
            No features configured
          </p>
        )}
      </div>
    </div>
  );
}

function Plans() {
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [isAddPlanModalOpen, setIsAddPlanModalOpen] = useState(false);
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false);
  const [newPlan, setNewPlan] = useState({ planName: "" });
  const [userPlans, setUserPlans] = useState([]);
  const [subscriptionType, setSubscriptionType] = useState("FREE");
  const [isManageUserPlansModalOpen, setIsManageUserPlansModalOpen] =
    useState(false);
  const [featureInputs, setFeatureInputs] = useState([
    { featureName: "", featureDescription: "" },
  ]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const addUserRange = () => {
    setUserPlans([
      ...userPlans,
      { userPlanId: 0, minimumUser: "", maximumUser: "", basePlan: false },
    ]);
  };

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const response = await axiosInstance.get("/subscription-plans");
        setSubscriptionPlans(response.data?.data || []);
        if (!response.data?.data)
          toast.error("Failed to fetch subscription plans.");
      } catch (error) {
        toast.error(
          `An error occurred: ${error.response?.data?.message || error.message}`,
        );
      }
    };
    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    const fetchUserPlans = async () => {
      try {
        const response = await axiosInstance.get("/user-plans");
        setUserPlans(
          Array.isArray(response.data?.data) ? response.data.data : [],
        );
      } catch (error) {
        toast.error(
          `An error occurred: ${error.response?.data?.message || error.message}`,
        );
      }
    };
    fetchUserPlans();
  }, []);

  const openAddPlanModal = () => {
    setNewPlan({ planName: "" });
    setFeatureInputs([{ featureName: "", featureDescription: "" }]);
    setIsAddPlanModalOpen(true);
  };

  const openEditPlanModal = (plan) => {
    setSelectedPlan(plan);
    setNewPlan({ planName: plan.planName });
    setFeatureInputs(
      plan.planFeatures.map((f) => ({
        featureName: f.featureName || "",
        featureDescription: f.featureDescription || "",
      })),
    );
    setIsEditPlanModalOpen(true);
  };

  const handleFeatureInputChange = (index, field, value) => {
    const updated = [...featureInputs];
    updated[index][field] = value || "";
    setFeatureInputs(updated);
  };

  const handleAddPlanSubmit = async () => {
    const newPlanWithFeatures = {
      planId: 0,
      planName: newPlan.planName,
      subscriptionType,
      planSequence: subscriptionPlans.length + 1,
      planFeatures: featureInputs.map((f) => ({
        featureName: f.featureName.trim(),
        featureDescription: f.featureDescription.trim(),
      })),
    };
    try {
      const response = await axiosInstance.post(
        "/subscription-plans",
        newPlanWithFeatures,
      );
      setSubscriptionPlans((prev) => [...prev, response.data?.data]);
      setFeatureInputs([{ featureName: "", featureDescription: "" }]);
      setNewPlan({ planName: "" });
      setIsAddPlanModalOpen(false);
      toast.success("Plan added successfully!");
    } catch (error) {
      toast.error(
        error.response?.data.status === 409
          ? error.response?.data.message
          : "Something went wrong",
      );
    }
  };

  const handleEditPlanSubmit = async () => {
    const updatedPlan = {
      moduleId: 0,
      subscriptionPlans: [
        {
          subscriptionPlanId: selectedPlan.planId,
          planName: newPlan.planName.trim(),
          planSequence: selectedPlan.planSequence,
          userPlans: [
            { userPlanId: 0, minUser: 1, maxUser: 100, planAmount: 50 },
          ],
          moduleFeatureIDs: featureInputs
            .filter((f) => f.featureName.trim() && f.featureDescription.trim())
            .map((f) => f.featureName),
        },
      ],
      priceAllocatedFrom: "2024-11-24",
      priceAllocatedTo: "2024-11-24",
    };
    try {
      await axiosInstance.put(
        `/subscription-plans/${selectedPlan.planId}`,
        updatedPlan,
      );
      setIsEditPlanModalOpen(false);
      toast.success("Plan updated successfully!");
    } catch {
      toast.error("Failed to update plan.");
    }
  };

  const handleBasePlanChange = (selectedIndex) => {
    setUserPlans(
      userPlans.map((plan, index) => ({
        ...plan,
        basePlan: index === selectedIndex,
      })),
    );
  };

  const handleUserPlanInputChange = (index, field, value) => {
    const updated = [...userPlans];
    updated[index][field] =
      field === "minimumUser" || field === "maximumUser"
        ? value === ""
          ? ""
          : parseInt(value) || ""
        : value;
    setUserPlans(updated);
  };

  // Shared feature inputs UI
  const FeatureInputList = () => (
    <div className="space-y-1.5">
      {featureInputs.map((input, index) => (
        <div
          key={index}
          className="flex gap-2 items-center bg-gray-50 p-2 rounded-lg border border-gray-200"
        >
          <div className="flex-1 grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Feature name"
              value={input.featureName}
              onChange={(e) =>
                handleFeatureInputChange(index, "featureName", e.target.value)
              }
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              placeholder="Description"
              value={input.featureDescription}
              onChange={(e) =>
                handleFeatureInputChange(
                  index,
                  "featureDescription",
                  e.target.value,
                )
              }
              className="px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {featureInputs.length > 1 && (
            <button
              onClick={() =>
                setFeatureInputs((prev) => prev.filter((_, i) => i !== index))
              }
              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
      {/* Add Plan Modal */}
      <Modal
        isOpen={isAddPlanModalOpen}
        onClose={() => setIsAddPlanModalOpen(false)}
      >
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <div className="p-1.5 bg-blue-100 rounded-lg">
              <svg
                className="w-4 h-4 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <h2 className="text-base font-bold text-gray-800">Add New Plan</h2>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                Plan Name
              </label>
              <input
                type="text"
                placeholder="Enter plan name"
                value={newPlan.planName}
                onChange={(e) =>
                  setNewPlan({ ...newPlan, planName: e.target.value })
                }
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
                Subscription Type
              </label>
              <select
                value={subscriptionType}
                onChange={(e) => setSubscriptionType(e.target.value)}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="FREE">FREE</option>
                <option value="ENTERPRISE">ENTERPRISE</option>
                <option value="PRO">PRO</option>
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-500 tracking-wide">
                Plan Features
              </label>
              <button
                onClick={() =>
                  setFeatureInputs([
                    ...featureInputs,
                    { featureName: "", featureDescription: "" },
                  ])
                }
                className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
              >
                + Add Feature
              </button>
            </div>
            <FeatureInputList />
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => setIsAddPlanModalOpen(false)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleAddPlanSubmit}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Add Plan
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Plan Modal */}
      <Modal
        isOpen={isEditPlanModalOpen}
        onClose={() => setIsEditPlanModalOpen(false)}
      >
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <div className="p-1.5 bg-amber-100 rounded-lg">
              <svg
                className="w-4 h-4 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <h2 className="text-base font-bold text-gray-800">Edit Plan</h2>
            {selectedPlan && (
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {selectedPlan.planName}
              </span>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 tracking-wide mb-1">
              Plan Name
            </label>
            <input
              type="text"
              placeholder="Enter plan name"
              value={newPlan.planName}
              onChange={(e) =>
                setNewPlan({ ...newPlan, planName: e.target.value })
              }
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold text-gray-500 tracking-wide">
                Plan Features
              </label>
              <button
                onClick={() =>
                  setFeatureInputs([
                    ...featureInputs,
                    { featureName: "", featureDescription: "" },
                  ])
                }
                className="text-xs text-blue-600 hover:text-blue-700 font-medium hover:underline"
              >
                + Add Feature
              </button>
            </div>
            <FeatureInputList />
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={() => setIsEditPlanModalOpen(false)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleEditPlanSubmit}
              className="flex-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      {/* Manage User Plans Modal */}
      <Modal
        isOpen={isManageUserPlansModalOpen}
        onClose={() => setIsManageUserPlansModalOpen(false)}
      >
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <div className="p-1.5 bg-green-100 rounded-lg">
              <svg
                className="w-4 h-4 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <h2 className="text-base font-bold text-gray-800">
              Manage User Plans
            </h2>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {userPlans.map((plan, index) => (
              <div
                key={plan.userPlanId}
                className="bg-gray-50 border border-gray-200 rounded-lg p-2.5"
              >
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">
                      Min Users
                    </label>
                    <input
                      type="number"
                      placeholder="0"
                      value={plan.minimumUser}
                      onChange={(e) =>
                        handleUserPlanInputChange(
                          index,
                          "minimumUser",
                          e.target.value,
                        )
                      }
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">
                      Max Users
                    </label>
                    <input
                      type="number"
                      placeholder="0"
                      value={plan.maximumUser}
                      onChange={(e) =>
                        handleUserPlanInputChange(
                          index,
                          "maximumUser",
                          e.target.value,
                        )
                      }
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={plan.basePlan}
                      onChange={() => handleBasePlanChange(index)}
                      className="w-3.5 h-3.5 text-blue-600 rounded"
                    />
                    <span className="text-xs font-medium text-gray-600">
                      Base Plan
                    </span>
                  </label>
                  <button
                    onClick={async () => {
                      try {
                        const response = await axiosInstance.delete(
                          `/user-plans/${plan.userPlanId}`,
                        );
                        if (response.data?.status === 0) {
                          setUserPlans(
                            userPlans.filter(
                              (item) => item.userPlanId !== plan.userPlanId,
                            ),
                          );
                          toast.success("User plan deleted!");
                        } else {
                          toast.error("Failed to delete user plan.");
                        }
                      } catch {
                        toast.error("Failed to delete user plan.");
                      }
                    }}
                    className="px-2.5 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-md transition-colors flex items-center gap-1"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button
              onClick={addUserRange}
              className="flex-1 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center justify-center gap-1.5"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Range
            </button>
            <button
              onClick={async () => {
                try {
                  const response = await axiosInstance.post(
                    "/user-plans",
                    userPlans,
                  );
                  setUserPlans(response.data?.data || []);
                  toast.success("User plans updated!");
                  setIsManageUserPlansModalOpen(false);
                } catch {
                  toast.error("Failed to update user plans.");
                }
              }}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
            >
              Save Changes
            </button>
          </div>
        </div>
      </Modal>

      <div className="space-y-4">
        {/* Subscription Plans Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">
                Subscription Plans
              </h2>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {subscriptionPlans.length} plans
              </span>
            </div>
            <button
              onClick={openAddPlanModal}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-1.5 px-4 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Plan
            </button>
          </div>

          {subscriptionPlans.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {subscriptionPlans.map((plan) => (
                <PlanItem
                  key={plan.planId}
                  plan={plan}
                  onEdit={openEditPlanModal}
                  onDelete={(id) => console.log("Delete plan:", id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm font-medium text-gray-500">
                No subscription plans available
              </p>
            </div>
          )}
        </div>

        <hr className="border-gray-200" />

        {/* User Plans Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">User Plans</h2>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {userPlans.length} ranges
              </span>
            </div>
            <button
              onClick={() => setIsManageUserPlansModalOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold py-1.5 px-4 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              Manage
            </button>
          </div>

          {userPlans.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {userPlans.map((plan) => (
                <div
                  key={plan.userPlanId}
                  className="bg-gradient-to-br from-green-50 to-blue-50 border border-green-200 rounded-lg px-3 py-2.5 flex items-center justify-between hover:shadow-sm transition-all"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-700">
                      {plan.minimumUser}–{plan.maximumUser}{" "}
                      <span className="text-xs font-normal text-gray-400">
                        users
                      </span>
                    </span>
                  </div>
                  {plan.basePlan && (
                    <span className="px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full font-medium flex-shrink-0">
                      Base
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-sm font-medium text-gray-500">
                No user plans configured
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Plans;
